<template>
  <b-form
    id="self-serve-registration-form"
    class="mt-4"
    @submit="onSubmit"
    novalidate
  >
    <h2>Start your Plan</h2>
    <PlanListDropdownVue
      :selectedValue="initialPlanCode"
      @setSelectedPlan="selectPlan"
    />
    <b-row class="mt-4">
      <b-col lg="10">
        <b-form-group
          label="Company Name"
          :invalid-feedback="errorMessage('company_name')"
        >
          <b-form-input
            v-model="form.company_name"
            :state="isValid('company_name')"
          >
          </b-form-input>
        </b-form-group>
        <b-row>
          <b-col lg="6">
            <b-form-group
              label="First Name"
              :invalid-feedback="errorMessage('first_name')"
            >
              <b-form-input
                v-model="form.first_name"
                :state="isValid('first_name')"
              >
              </b-form-input>
            </b-form-group>
          </b-col>
          <b-col lg="6">
            <b-form-group
              label="Last Name"
              :invalid-feedback="errorMessage('last_name')"
            >
              <b-form-input
                v-model="form.last_name"
                :state="isValid('last_name')"
              >
              </b-form-input>
            </b-form-group>
          </b-col>
        </b-row>
        <b-form-group label="Mobile Number" :state="isValid('mobile_number')">
          <b-input-group prepend="+63">
            <b-form-input
              v-model="form.mobile_number"
              maxlength="10"
              inputmode="numeric"
              pattern="[0-9]*"
            />
          </b-input-group>
          <b-form-invalid-feedback :force-show="!isValid('mobile_number')">{{
            errorMessage("mobile_number")
          }}</b-form-invalid-feedback>
        </b-form-group>
        <b-form-group
          label="Email Address"
          :invalid-feedback="errorMessage('email')"
        >
          <b-form-input
            type="email"
            v-model="form.email"
            :state="isValid('email')"
          >
          </b-form-input>
        </b-form-group>
        <b-form-group
          label="Confirm Email Address"
          :invalid-feedback="errorMessage('confirm_email')"
        >
          <b-form-input
            type="email"
            v-model="form.confirm_email"
            :state="isValid('confirm_email')"
          >
          </b-form-input>
        </b-form-group>
        <b-form-group :invalid-feedback="errorMessage('tin_number')">
          <template #label>
            TIN (Optional)
            <b-icon id="tin-tooltip" icon="question-circle-fill" class="ml-1" />
            <b-popover
              container="self-serve-registration-form"
              target="tin-tooltip"
              triggers="hover"
            >
              <span class="font-weight-bold">What is the TIN number for? </span>
              This will be used for registered customers who will need Officials
              Receipt and CWT.
            </b-popover>
          </template>
          <b-form-input
            v-model="form.tin_number"
            :state="isValid('tin_number')"
            maxlength="12"
            inputmode="numeric"
            pattern="[0-9]*"
          />
        </b-form-group>
        <b-form-group :invalid-feedback="errorMessage('referral_code')">
          <template #label> Referral Code (Optional) </template>
          <b-form-input
            v-model="form.referral_code"
            :state="isValid('referral_code')"
            maxlength="8"
          />
        </b-form-group>
        <b-form-group>
          <div
            id="agreement"
            class="d-flex align-items-center flex-wrap tm-trigger"
            @click="$bvModal.show('terms-conditions-privacy-policy')"
          >
            <b-form-checkbox
              v-model="form.terms_and_condition"
              :value="true"
              :unchecked-value="false"
              disabled
            >
            </b-form-checkbox>
            I agree to the<strong class="ml-1"
              >Terms &amp; Conditions and Privacy Policy</strong
            >
          </div>
        </b-form-group>
        <b-form-group>
          <b-overlay :show="loading">
            <b-button
              type="submit"
              block
              pill
              variant="primary"
              :disabled="isSubmitDisabled"
            >
              Get this plan now
            </b-button>
          </b-overlay>
        </b-form-group>
      </b-col>
    </b-row>
    <TermsAndConditionsModalVue
      @userAgreedToTermsConditionsPrivacyPolicy="setTermsAndConditions"
      :code="selectedPlan && selectedPlan.code"
      :isAlreadyAgreed="form.terms_and_condition"
    />
  </b-form>
</template>

<script>
import _ from "lodash";
import { mapState, mapMutations, mapGetters, mapActions } from "vuex";
import PlanListDropdownVue from "./PlanListDropdown";
import TermsAndConditionsModalVue from "./TermsAndConditionsModal";

function initForm() {
  return {
    plan_uuid: null,
    company_name: null,
    first_name: null,
    last_name: null,
    mobile_number: null,
    email: null,
    confirm_email: null,
    tin_number: null,
    terms_and_condition: false,
    registration_channel: "Web",
    referral_code: null,
  };
}

export default {
  name: "RegistrationForm",
  components: { PlanListDropdownVue, TermsAndConditionsModalVue },
  data() {
    return {
      form: initForm(),
      selectedPlan: null,
    };
  },
  computed: {
    ...mapState("auth", ["loading", "errors"]),
    ...mapGetters({
      planList: "auth/planList",
      authErrors: "auth/authErrors",
      isBypassPayment: "auth/isBypassPayment"
    }),
    ...mapGetters("promotional", ["promoErrors", "promoCodeDetails"]),
    isSubmitDisabled: {
      get() {
        const { tin_number, referral_code, ...requiredFields } = this.form; //eslint-disable-line
        const formValues = Object.values(requiredFields);
        return (
          this.loading ||
          formValues.includes(null) ||
          formValues.includes(false)
        );
      },
      set(isDisabled) {
        return isDisabled;
      },
    },
    planCode() {
      return this.$route.params.plan
        ? this.$route.params.plan
        : this.$route.query.plan
        ? this.$route.query.plan
        : null;
    },
    initialPlanCode() {
      return this.planList.length
        ? this.planCode
          ? this.planCode
          : this.planList[0].code
        : "";
    },
    isSalesRepCode: {
      get() {
        const params = Object.keys(this.$route.query);
        return params.length > 0 && params.includes("sales_rep_code");
      },
      set(status) {
        return status;
      },
    },
    salesRepCode: {
      get() {
        return this.$route.query.sales_rep_code;
      },
      set(code) {
        return code;
      },
    },
  },
  methods: {
    ...mapMutations({
      setErrors: "auth/errors",
    }),
    ...mapActions({
      registerValidate: "auth/registerValidate",
      registerUser: "auth/registerUser",
    }),
    selectPlan(plan) {
      this.form.plan_uuid = plan.uuid;
      this.selectedPlan = plan;
    },
    isValidEmail(email) {
      const isEmail = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/; //eslint-disable-line
      return isEmail.test(email);
    },
    isValidMobile(mobile) {
      const isNumber = /\d{10}$/;
      const firstDigit = mobile.slice(0, 1);
      return firstDigit === "9" && isNumber.test(mobile);
    },
    isNumber(e) {
      if (/\D/g.test(String.fromCharCode(e.keyCode))) e.preventDefault();
    },
    isAlphaNumeric(input) {
      return /[^A-Za-z0-9\s]+/g.test(input);
    },
    filterAlphaNumeric(e) {
      if (this.isAlphaNumeric(String.fromCharCode(e.keyCode)))
        e.preventDefault();
    },
    setTermsAndConditions(value) {
      this.form.terms_and_condition = value;
    },
    onSubmit: async function (evt) {
      evt.preventDefault();
      const form = this.form;
      if (this.isSalesRepCode) form["sales_rep_code"] = this.salesRepCode;
      const { confirm_email, ...payload } = form; //eslint-disable-line
      const errors = {};
      const optionalFields = ["tin_number", "referral_code"];

      Object.keys(form).forEach((key) => {
        if (!form[key] && !optionalFields.includes(key))
          errors[key] = "This field is required.";
        if (key === "email" && form[key] && !this.isValidEmail(form[key]))
          errors["email"] = "Please enter a valid email address.";
        if (
          key === "confirm_email" &&
          form[key] &&
          this.isValidEmail(form.email) &&
          form[key] !== form.email
        )
          errors[key] = "Email address does not match.";
        if (
          key === "mobile_number" &&
          form[key] &&
          !this.isValidMobile(form[key])
        )
          errors[key] = "Please enter a valid mobile number.";
        if (key === "tin_number" && form[key] && form[key].length < 9)
          errors[key] = "Please enter a valid TIN.";
      });

      if (this.form.referral_code) {
        await this.$store.dispatch("promotional/verifyPromoCode", {
          code: this.form.referral_code,
          plan_uuid: this.form.plan_uuid,
          type: "referral_code",
        });

        if (this.promoErrors)
          errors["referral_code"] = "This referral code is invalid.";
      }

      this.setErrors(errors);

      if (Object.keys(errors).length === 0) {
        await this.registerValidate(payload).then(async () => {
          if (!this.errors || Object.keys(this.errors).length === 0) {
            if (
              this.selectedPlan &&
              (
                this.selectedPlan.is_free &&
                this.selectedPlan.range_type_free !== "unlimited" &&
                !this.isBypassPayment
              ) ||
              (
                !this.selectedPlan.is_free &&
                !this.isBypassPayment
              )
              
            )
              this.$router.push({ name: "Checkout" });
            else {
              const registrationData = _.merge(
                _.omit(this.form, ["confirm_email"]),
                {
                  billing_address: "-",
                  billing_recurrence: null,
                  charge_amount: Number(
                    parseFloat(
                      this.planList.find(
                        (plan) => plan.uuid === this.form.plan_uuid
                      ).price
                    ).toFixed(2)
                  ),
                  tokenization_data: null,
                }
              );
              await this.registerUser(registrationData);
              if (_.isEmpty(this.authErrors))
                this.$router.replace({ name: "Success" });
            }
          }
        });
      }
    },
    errorMessage: function (field) {
      if (this.errors == null) {
        return "";
      }
      return this.errors[field];
    },
    isValid: function (field) {
      if (this.errors == null) {
        return null;
      }
      return this.errors[field] ? false : null;
    },
  },
  watch: {
    planList(list) {
      if (list.length) {
        if (this.planCode) {
          const plan = list.find((plan) => plan.code === this.planCode);

          if (plan) this.selectPlan(plan);
          else this.$router.push({ name: "Home" });
        } else this.selectPlan(list[0]);
      }
    },
    "form.mobile_number"(value) {
      this.$nextTick(() => {
        this.form.mobile_number = value.replace(/\D/g, "");
      });
    },
    "form.tin_number"(value) {
      this.$nextTick(() => {
        this.form.tin_number = value.replace(/\D/g, "");
      });
    },
    "form.referral_code"(value) {
      this.$nextTick(() => {
        this.form.referral_code = value.trim().replace(/[^A-Za-z0-9\s]/g, "");
      });
    },
    selectedPlan() {
      if (this.form.terms_and_condition)
        this.form.terms_and_condition = false;
    }
  },
};
</script>

<style lang="scss" scoped>
#self-serve-registration-form {
  h2 {
    font-size: 30px;
    font-family: 'SKModernistBold', sans-serif;
    color: #2f3941;
  }
  .tm-trigger {
    cursor: pointer;
    strong {
      color: #ff830e;
    }
  }
  .btn.btn-primary {
    font-family: 'SKModernistBold', sans-serif;
    font-size: 16px;
    height: 55px;
    &.disabled {
      background: #e9ebed;
      color: #87929d;
      border: 0;
    }
  }
  #tin-tooltip {
    color: #e2e2e2;
    &:hover {
      color: #5182ff;
    }
  }
  #agreement {
    font-size: 14px;
  }
  @media (max-width: 767px) {
    #agreement {
      font-size: 13px;
    }
  }
}
</style>

<style lang="scss">
#self-serve-registration-form {
  .col-form-label {
    font-size: 14px;
    font-family: 'SKModernistBold', sans-serif;
  }
  .form-control {
    font-size: 16px;
  }
  .custom-control-input:checked ~ .custom-control-label::before {
    border-color: #ff830e;
    background-color: #ff830e !important;
  }
  .custom-control-input[disabled] ~ .custom-control-label::before,
  .custom-control-input:disabled ~ .custom-control-label::before {
    background-color: transparent;
  }
  .form-group.is-invalid {
    .input-group {
      border: solid 1px #dc3545;
      border-radius: 0.25rem;
      .input-group-text,
      .form-control {
        border-color: transparent;
      }
    }
  }
  .popover {
    max-width: 415px;
    .arrow {
      display: none;
    }
  }
  @media (max-width: 767px) {
    .popover {
      max-width: 276px;
    }
  }
}
</style>
